import * as React from 'react';
import * as styles from './dashboards.module.scss';
import { PaginatedDashboardContext } from '../../../../shared/paginated-dashboard-context';
import usePaginatedDashboards from '../../../../hooks/usePaginatedDashboards';
import useSearch from '../../../../hooks/useSearch';
import useMeasuredRef from '../../../../hooks/useMeasuredRef';
import CircularProgress from '@material-ui/core/CircularProgress';
import DashboardTable from './table/dashboard-table';
import Pagination from '../../pagination/pagination';
import SearchBar from '../../../common/search-bar';
import PriorityTwoFailure from '../../../announcement/priority-two-failure';
import SecondaryButton from '../../../common/secondary-button';
import { Redirect } from 'react-router';
import Paths from '../../../../models/enums/paths';

interface Props {
  clientId: number;
}

const Dashboards = ({ clientId }: Props) => {
  const { pageSize, currentPage, orderColumn, orderDirection } = React.useContext(PaginatedDashboardContext);
  const { loading, results, totalResults, fetchDashboards, errorMessage } = usePaginatedDashboards();
  const { searchQuery: searchTerm, updateSearchQuery, setIsSearching } = useSearch();
  const announcementCount: number = 0; //Announcements are not shown on management UI, so there is no need to get the real count
  const { measuredRef, dimensions } = useMeasuredRef([results, announcementCount, errorMessage], true);
  const [redirectToAddDashboard, setRedirectToAddDashboard] = React.useState(false);

  // Fetches the dashboard information for the page on mount
  React.useEffect(() => {
    fetchDashboards({ currentPage, pageSize, searchTerm, clientId, orderColumn, orderDirection });
  }, []);

  if (redirectToAddDashboard) {
    return <Redirect push to={{ pathname: Paths.AddClientDashboard.replace(':clientId', clientId.toString()) }} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1>Dashboards</h1>
          <SecondaryButton
            text="Add a Dashboard"
            title="Add a Dashboard"
            handleClick={() => setRedirectToAddDashboard(true)}
          />
        </div>
        <SearchBar
          value={searchTerm}
          updateValue={(val: string) => updateSearchQuery(val)}
          setIsSearching={(searching: boolean) => setIsSearching(searching)}
          placeholder={'Search Dashboards'}
          submitHandler={() =>
            fetchDashboards({
              currentPage: 1,
              pageSize: pageSize,
              searchTerm,
              clientId,
              orderColumn,
              orderDirection
            })
          }
        />
      </div>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      {loading && results.length === 0 ? (
        <div className={styles.circularProgress}>
          <CircularProgress />
        </div>
      ) : !results || results.length === 0 ? (
        <span>There are no Dashboards.</span>
      ) : (
        <div
          className={styles.center}
          ref={measuredRef}
          style={{ height: `calc(100vh - ${dimensions ? 20 + dimensions.top : 0}px` }}
        >
          {(loading && results.length > 0) || (loading && results.length === 0) ? (
            <div className={styles.circularProgress}>
              <CircularProgress />
            </div>
          ) : null}
          <div className={styles.content}>
            <div>
              <span className={styles.resultsCount}>{totalResults} results</span>
            </div>
            {<DashboardTable clientId={clientId} loading={loading} />}
          </div>
          <Pagination
            fetchResults={fetchDashboards}
            paginatedContext={PaginatedDashboardContext}
            searchTerm={searchTerm}
            fetchExtraProps={{ clientId: clientId }}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboards;
